<template>
    <CRow>
        <CCol>
            <CCard>
                <CCardHeader>
                    <strong>Вебхуки iiko</strong>
                </CCardHeader>
                <CCardBody>
                    <div v-if="loading" class="text-center py-4">Загрузка...</div>
                    <template v-else>
                        <CAlert v-if="reregisterError" color="danger" class="mb-3" dismissible @close="reregisterError = null">
                            {{ reregisterError }}
                        </CAlert>

                        <div class="mb-4 p-3 border border-gray-300 rounded-md bg-gray-50">
                            <div class="flex items-center justify-between">
                                <div>
                                    <div class="font-bold mb-1">Режим обработки заказов</div>
                                    <div class="text-sm text-gray-600">
                                        <span v-if="webhooksEnabled">
                                            Вебхуки активны — iiko присылает события в реальном времени. Крон работает как fallback (раз в 2 мин).
                                        </span>
                                        <span v-else>
                                            Вебхуки отключены — статусы заказов обновляются только через крон (раз в 2 мин).
                                        </span>
                                    </div>
                                    <div v-if="webhookUrl" class="text-xs text-gray-500 mt-1">
                                        URL: <code>{{ webhookUrl }}</code>
                                    </div>
                                    <div v-else class="text-xs text-red-500 mt-1">
                                        IIKO_WEBHOOK_URL не задан — регистрация невозможна
                                    </div>
                                </div>
                                <CBadge :color="webhooksEnabled ? 'success' : 'secondary'" class="px-3 py-1 text-sm">
                                    {{ webhooksEnabled ? 'Вебхуки' : 'Крон' }}
                                </CBadge>
                            </div>
                        </div>

                        <div class="overflow-hidden border border-gray-300 rounded-md">
                            <!-- header -->
                            <div class="flex items-center bg-gray-100 text-sm font-bold">
                                <div class="w-1/5 py-2 pl-3">Партнёр</div>
                                <div class="w-1/4 py-2 pl-2">Organization ID</div>
                                <div class="w-1/8 py-2 pl-2">Тип</div>
                                <div class="w-1/6 py-2 pl-2">Статус хука</div>
                                <div class="py-2 pl-2 ml-auto pr-2">Действия</div>
                            </div>

                            <template v-for="partner in partners">
                                <!-- partner row -->
                                <div :key="partner.alias" class="flex items-center px-0 py-2 border-t border-gray-300">
                                    <div class="w-1/5 pl-3">
                                        <span class="font-medium">{{ partner.name }}</span>
                                        <span class="text-xs text-gray-400 ml-1">({{ partner.alias }})</span>
                                    </div>
                                    <div class="w-1/4 pl-2">
                                        <code class="text-xs">{{ partner.organizationId || '—' }}</code>
                                    </div>
                                    <div class="w-1/8 pl-2">
                                        <CBadge :color="partner.supportsWebhooks ? 'success' : 'secondary'">
                                            {{ partner.supportsWebhooks ? 'Transport' : 'Biz' }}
                                        </CBadge>
                                    </div>
                                    <div class="w-1/6 pl-2">
                                        <template v-if="partner.supportsWebhooks">
                                            <CBadge v-if="!webhooksEnabled" color="secondary">Не активен</CBadge>
                                            <CBadge v-else-if="partner.registered === true" color="success">Зарегистрирован</CBadge>
                                            <template v-else-if="partner.registered === false">
                                                <CBadge color="danger">Ошибка</CBadge>
                                                <div v-if="partner.registrationError" class="text-xs text-red-600 mt-1 break-all">
                                                    {{ partner.registrationError }}
                                                </div>
                                            </template>
                                            <CBadge v-else color="warning">Не проверен</CBadge>
                                        </template>
                                        <span v-else class="text-xs text-gray-400">н/д</span>
                                    </div>
                                    <div class="ml-auto pr-2 flex items-center gap-1">
                                        <CButton
                                            v-if="partner.supportsWebhooks"
                                            size="sm"
                                            color="info"
                                            variant="outline"
                                            :disabled="loadingInfoAliases.includes(partner.alias)"
                                            :title="`Информация о ${partner.name}`"
                                            @click="togglePartnerInfo(partner.alias)"
                                        >
                                            {{ loadingInfoAliases.includes(partner.alias) ? '...' : 'ℹ' }}
                                        </CButton>
                                        <CButton
                                            v-if="partner.supportsWebhooks"
                                            size="sm"
                                            color="secondary"
                                            variant="outline"
                                            :disabled="!webhooksEnabled || !webhookUrl || reregisteringAliases.includes(partner.alias)"
                                            :title="`Перерегистрировать ${partner.name}`"
                                            @click="reregisterOne(partner.alias)"
                                        >
                                            {{ reregisteringAliases.includes(partner.alias) ? '...' : '↺' }}
                                        </CButton>
                                    </div>
                                </div>

                                <!-- expandable info panel -->
                                <div
                                    :key="partner.alias + '_info'"
                                    v-if="partnerInfo[partner.alias]"
                                    class="border-t border-blue-200 bg-blue-50 px-4 py-3 text-sm"
                                >
                                    <div v-if="partnerInfo[partner.alias].error" class="text-red-600">
                                        {{ partnerInfo[partner.alias].error }}
                                    </div>
                                    <template v-else>
                                        <!-- Webhook settings -->
                                        <div class="mb-3">
                                            <div class="font-semibold mb-1 text-gray-700">Webhook settings <span class="font-normal text-gray-400 text-xs">(/api/1/webhooks/settings)</span></div>
                                            <div v-if="partnerInfo[partner.alias].webhookSettings.error" class="text-red-500 text-xs">{{ partnerInfo[partner.alias].webhookSettings.error }}</div>
                                            <template v-else>
                                                <div class="flex gap-4 flex-wrap">
                                                    <div>
                                                        <span class="text-gray-500">webHooksUri:</span>
                                                        <code class="ml-1 text-xs break-all">{{ partnerInfo[partner.alias].webhookSettings.data.webHooksUri || '—' }}</code>
                                                        <CBadge
                                                            v-if="webhookUrl"
                                                            :color="partnerInfo[partner.alias].webhookSettings.data.webHooksUri === webhookUrl ? 'success' : 'danger'"
                                                            class="ml-2"
                                                        >
                                                            {{ partnerInfo[partner.alias].webhookSettings.data.webHooksUri === webhookUrl ? 'наш' : 'чужой / не задан' }}
                                                        </CBadge>
                                                    </div>
                                                    <div>
                                                        <span class="text-gray-500">authToken:</span>
                                                        <code class="ml-1 text-xs">{{ partnerInfo[partner.alias].webhookSettings.data.authToken || '—' }}</code>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>

                                        <!-- Payment types -->
                                        <div class="mb-3">
                                            <div class="font-semibold mb-1 text-gray-700">Payment types <span class="font-normal text-gray-400 text-xs">(/api/1/payment_types)</span></div>
                                            <div v-if="partnerInfo[partner.alias].paymentTypes.error" class="text-red-500 text-xs">{{ partnerInfo[partner.alias].paymentTypes.error }}</div>
                                            <template v-else>
                                                <table class="text-xs w-full border-collapse">
                                                    <thead>
                                                        <tr class="bg-gray-100">
                                                            <th class="text-left py-1 px-2 border border-gray-200">ID</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Code</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Name</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Kind</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr
                                                            v-for="pt in partnerInfo[partner.alias].paymentTypes.data.paymentTypes"
                                                            :key="pt.id"
                                                            class="even:bg-gray-50"
                                                        >
                                                            <td class="py-1 px-2 border border-gray-200 font-mono">{{ pt.id }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ pt.code }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ pt.name }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ pt.paymentTypeKind }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </template>
                                        </div>

                                        <!-- Order types -->
                                        <div class="mb-3">
                                            <div class="font-semibold mb-1 text-gray-700">Order types <span class="font-normal text-gray-400 text-xs">(/api/1/deliveries/order_types)</span></div>
                                            <div v-if="partnerInfo[partner.alias].orderTypes.error" class="text-red-500 text-xs">{{ partnerInfo[partner.alias].orderTypes.error }}</div>
                                            <template v-else>
                                                <table class="text-xs w-full border-collapse">
                                                    <thead>
                                                        <tr class="bg-gray-100">
                                                            <th class="text-left py-1 px-2 border border-gray-200">ID</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Name</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">ServiceType</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Default</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr
                                                            v-for="ot in flatOrderTypes(partnerInfo[partner.alias].orderTypes.data.orderTypes)"
                                                            :key="ot.id"
                                                            class="even:bg-gray-50"
                                                        >
                                                            <td class="py-1 px-2 border border-gray-200 font-mono">{{ ot.id }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ ot.name }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ ot.orderServiceType }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ ot.isDefault ? 'да' : '' }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </template>
                                        </div>

                                        <!-- Terminal groups -->
                                        <div>
                                            <div class="font-semibold mb-1 text-gray-700">Terminal groups <span class="font-normal text-gray-400 text-xs">(/api/1/terminal_groups)</span></div>
                                            <div v-if="partnerInfo[partner.alias].terminalGroups.error" class="text-red-500 text-xs">{{ partnerInfo[partner.alias].terminalGroups.error }}</div>
                                            <template v-else>
                                                <table class="text-xs w-full border-collapse">
                                                    <thead>
                                                        <tr class="bg-gray-100">
                                                            <th class="text-left py-1 px-2 border border-gray-200">ID</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">Name</th>
                                                            <th class="text-left py-1 px-2 border border-gray-200">TimeZone</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr
                                                            v-for="tg in flatTerminalGroups(partnerInfo[partner.alias].terminalGroups.data.terminalGroups)"
                                                            :key="tg.id"
                                                            class="even:bg-gray-50"
                                                        >
                                                            <td class="py-1 px-2 border border-gray-200 font-mono">{{ tg.id }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ tg.name }}</td>
                                                            <td class="py-1 px-2 border border-gray-200">{{ tg.timeZone }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </template>
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </template>
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
export default {
    name: 'IikoHooks',

    data() {
        return {
            loading: true,
            reregisterError: null,
            reregisteringAliases: [],
            webhooksEnabled: true,
            webhookUrl: null,
            partners: [],
            partnerInfo: {},
            loadingInfoAliases: [],
        };
    },

    async created() {
        await this.fetchData();
    },

    methods: {
        async fetchData() {
            try {
                const { data } = await this.axios.get('/integration/iiko/hooks');
                this.webhooksEnabled = data.webhooksEnabled;
                this.webhookUrl = data.webhookUrl;
                this.partners = data.partners;
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },

        async togglePartnerInfo(alias) {
            if (this.partnerInfo[alias]) {
                const updated = { ...this.partnerInfo };
                delete updated[alias];
                this.partnerInfo = updated;
                return;
            }
            this.loadingInfoAliases = [...this.loadingInfoAliases, alias];
            try {
                const { data } = await this.axios.get(`/integration/iiko/hooks/partner-info/${alias}`, { timeout: 30000 });
                this.partnerInfo = { ...this.partnerInfo, [alias]: data };
            } catch (e) {
                this.partnerInfo = { ...this.partnerInfo, [alias]: { error: e?.response?.data?.error || e?.message || 'Ошибка загрузки' } };
            } finally {
                this.loadingInfoAliases = this.loadingInfoAliases.filter(a => a !== alias);
            }
        },

        flatOrderTypes(orgs) {
            if (!orgs) return [];
            return orgs.flatMap(org => org.items || []);
        },

        flatTerminalGroups(orgs) {
            if (!orgs) return [];
            return orgs.flatMap(org => org.items || []);
        },

        formatIikoError(raw) {
            if (!raw) return 'Неизвестная ошибка';
            if (raw.includes('TOO_MANY_REQUESTS')) {
                return 'iiko: превышен лимит запросов. Подождите ~5 минут и попробуйте снова.';
            }
            return raw;
        },

        async reregisterOne(alias) {
            this.reregisterError = null;
            this.reregisteringAliases = [...this.reregisteringAliases, alias];
            try {
                const { data } = await this.axios.post(`/integration/iiko/hooks/reregister/${alias}`, {}, { timeout: 720000 });
                this.partners = this.partners.map(p =>
                    p.alias === alias
                        ? { ...p, registered: data.registered, registrationError: data.error || null }
                        : p
                );
            } catch (e) {
                const raw = e?.response?.data?.error || e?.message || 'Неизвестная ошибка';
                this.reregisterError = `Ошибка перерегистрации ${alias}: ${this.formatIikoError(raw)}`;
                console.error(e);
            } finally {
                this.reregisteringAliases = this.reregisteringAliases.filter(a => a !== alias);
            }
        },
    },
};
</script>
